(function() {
  'use strict';

  if (!window.TypingText) {
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    function typeElement(el, options = {}) {
      const text = el.dataset.typingText || el.textContent.trim();
      if (!text) return Promise.resolve();

      const delay = options.delay ?? 0;
      const speed = options.speed ?? 72;

      if (prefersReduced) {
        el.textContent = text;
        return Promise.resolve();
      }

      el.dataset.typingText = text;

      return new Promise((resolve) => {
        setTimeout(() => {
          el.textContent = '';
          el.classList.add('typing-cursor');

          let index = 0;

          const tick = () => {
            if (index < text.length) {
              el.textContent += text.charAt(index);
              index += 1;
              setTimeout(tick, speed);
              return;
            }

            el.classList.remove('typing-cursor');
            resolve();
          };

          tick();
        }, delay);
      });
    }

    function initTyping(root, selector, options = {}) {
      const baseDelay = options.baseDelay ?? 200;
      const stagger = options.stagger ?? 100;
      const speed = options.speed ?? 72;

      root.querySelectorAll(selector).forEach((el, idx) => {
        if (!(el instanceof HTMLElement)) return;
        const text = el.textContent.trim();
        if (!text) return;
        el.dataset.typingText = text;
        typeElement(el, { delay: baseDelay + idx * stagger, speed });
      });
    }

    window.TypingText = { typeElement, initTyping };
  }

  let youtubeApiPromise = null;

  function loadYouTubeApi() {
    if (window.YT?.Player) return Promise.resolve(window.YT);
    if (youtubeApiPromise) return youtubeApiPromise;

    youtubeApiPromise = new Promise((resolve) => {
      const previous = window.onYouTubeIframeAPIReady;
      window.onYouTubeIframeAPIReady = () => {
        if (typeof previous === 'function') previous();
        resolve(window.YT);
      };

      if (!document.querySelector('script[src="https://www.youtube.com/iframe_api"]')) {
        const script = document.createElement('script');
        script.src = 'https://www.youtube.com/iframe_api';
        script.async = true;
        document.head.appendChild(script);
      }
    });

    return youtubeApiPromise;
  }

  class ProductShowcase {
    constructor(el) {
      this.root = el;
      this.track = el.querySelector('.ps-track');
      this.cards = Array.from(el.querySelectorAll('.ps-card'));
      this.prevBtn = el.querySelector('.ps-nav--prev .ps-nav-btn');
      this.nextBtn = el.querySelector('.ps-nav--next .ps-nav-btn');

      this.autoPlay = el.dataset.autoplay === 'true';
      this.interval = parseInt(el.dataset.interval, 10) || 5000;
      this.pauseOnHover = el.dataset.pauseHover === 'true';

      this.total = this.cards.length;
      if (this.total < 1 || !this.track) return;

      this.current = this.total > 2 ? 1 : 0;
      this.timer = null;
      this.animating = false;
      this.resizeRaf = null;
      this.youtubePlayers = new Map();
      this.soundEnabled = false;
      this.onResize = this.handleResize.bind(this);

      this.layout();
      this.bind();
      this.updateActiveInfo(false);
      this.initTyping();
      this.initMedia();
      this.manageVideos();
      if (this.autoPlay && this.total > 1) this.startAuto();
    }

    mod(n, m) {
      return ((n % m) + m) % m;
    }

    layout() {
      this.cards.forEach(card => {
        card.style.position = 'absolute';
        card.style.top = '0';
        card.style.left = '0';
        card.style.width = '100%';
        card.style.height = 'auto';
      });
      this.position();
    }

    position(animate) {
      const total = this.total;

      this.cards.forEach((card, i) => {
        const offset = this.getOffset(i);
        const abs = Math.abs(offset);

        card.style.transition = animate ? 'transform 0.5s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.5s ease, filter 0.5s ease' : 'none';

        if (abs > 2) {
          card.style.opacity = '0';
          card.style.pointerEvents = 'none';
          card.style.zIndex = '0';
          card.style.transform = `translateX(${offset > 0 ? 120 : -120}%) scale(0.8)`;
          return;
        }

        /* Side slides peek toward viewport edges to avoid empty margins */
        const spread = window.innerWidth >= 769 ? 88 : 72;
        const tx = offset * spread;
        const scale = abs === 0 ? 1 : (window.innerWidth >= 769 ? 0.88 : 0.85);
        const z = abs === 0 ? 10 : (abs === 1 ? 5 : 1);
        const op = abs === 0 ? 1 : (abs === 1 ? (window.innerWidth >= 769 ? 0.62 : 0.5) : 0);
        const sat = abs === 0 ? 1 : 0.35;
        const blur = abs === 0 ? '' : ' blur(1px)';

        card.style.transform = `translateX(${tx}%) scale(${scale})`;
        card.style.opacity = String(op);
        card.style.filter = `saturate(${sat})${blur}`.trim();
        card.style.zIndex = String(z);
        card.style.pointerEvents = abs <= 1 ? 'auto' : 'none';
        card.classList.toggle('ps-card--active', abs === 0);
      });
      this.updateSoundButtons();
    }

    getOffset(index) {
      let diff = index - this.current;
      if (this.total > 2) {
        if (diff > this.total / 2) diff -= this.total;
        if (diff < -this.total / 2) diff += this.total;
      }
      return diff;
    }

    go(dir) {
      if (this.animating) return;
      this.animating = true;
      this.soundEnabled = false;
      this.current = this.mod(this.current + dir, this.total);
      this.position(true);
      this.updateActiveInfo(true);
      this.manageVideos();
      this.updateSoundButtons();
      setTimeout(() => { this.animating = false; }, 520);
      this.resetAuto();
    }

    bind() {
      if (this.prevBtn) this.prevBtn.addEventListener('click', (e) => { e.stopPropagation(); this.go(-1); });
      if (this.nextBtn) this.nextBtn.addEventListener('click', (e) => { e.stopPropagation(); this.go(1); });

      this.root.querySelectorAll('[data-ps-unmute]').forEach((button) => {
        button.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.soundEnabled = !this.soundEnabled;
          this.applySoundToActive();
          this.updateSoundButtons();
        });
      });

      if (this.pauseOnHover) {
        this.root.addEventListener('mouseenter', () => this.stopAuto());
        this.root.addEventListener('mouseleave', () => {
          if (this.autoPlay) this.startAuto();
        });
      }

      /* Click on a card to navigate: click next/prev visible image to go to it */
      this.track.addEventListener('click', (e) => {
        if (this.total <= 1) return;
        if (e.target.closest('a')) return;
        const card = e.target.closest('.ps-card');
        if (!card) return;
        const idx = this.cards.indexOf(card);
        if (idx === -1) return;
        const offset = this.getOffset(idx);
        if (offset === -1) this.go(-1);
        else if (offset === 1) this.go(1);
      });

      let touchX = 0;
      let touchY = 0;
      let swiping = false;

      this.track.addEventListener('touchstart', e => {
        touchX = e.touches[0].clientX;
        touchY = e.touches[0].clientY;
        swiping = true;
      }, { passive: true });

      this.track.addEventListener('touchmove', e => {
        if (!swiping) return;
        const dx = Math.abs(e.touches[0].clientX - touchX);
        const dy = Math.abs(e.touches[0].clientY - touchY);
        if (dy > dx) swiping = false;
      }, { passive: true });

      this.track.addEventListener('touchend', e => {
        if (!swiping) return;
        const diff = touchX - e.changedTouches[0].clientX;
        if (Math.abs(diff) > 40) {
          diff > 0 ? this.go(1) : this.go(-1);
        }
        swiping = false;
      }, { passive: true });

      window.addEventListener('resize', this.onResize, { passive: true });
    }

    updateActiveInfo(animateText) {
      const card = this.cards[this.current];
      const info = this.root.querySelector('[data-ps-active-info]');
      if (!card || !info) return;

      const name = card.dataset.name || '';
      const price = card.dataset.price || '';
      const link = card.dataset.link || '#';
      const thumb = card.dataset.thumb || '';

      info.href = link;

      const nameEl = info.querySelector('[data-ps-active-name]');
      const priceEl = info.querySelector('[data-ps-active-price]');
      const thumbEl = info.querySelector('[data-ps-active-thumb]');
      const thumbWrap = info.querySelector('[data-ps-active-thumb-wrap]');

      if (nameEl) {
        nameEl.dataset.typingText = name;
        nameEl.textContent = animateText ? '' : name;
      }
      if (priceEl) {
        priceEl.dataset.typingText = price;
        priceEl.textContent = animateText ? '' : price;
      }
      if (thumb && thumbEl) {
        thumbEl.src = thumb;
        thumbEl.hidden = false;
      } else if (thumb && !thumbEl && thumbWrap) {
        const img = document.createElement('img');
        img.className = 'ps-card-thumb';
        img.src = thumb;
        img.alt = '';
        img.loading = 'lazy';
        img.draggable = false;
        img.dataset.psActiveThumb = '';
        thumbWrap.replaceWith(img);
      }

      if (animateText && window.TypingText) {
        if (nameEl) window.TypingText.typeElement(nameEl, { delay: 80, speed: 72 });
        if (priceEl) window.TypingText.typeElement(priceEl, { delay: 280, speed: 72 });
      }
    }

    isVisible() {
      const r = this.root.getBoundingClientRect();
      return r.top < window.innerHeight && r.bottom > 0;
    }

    initMedia() {
      this.cards.forEach((card, index) => {
        const video = card.querySelector('video');
        if (video) {
          video.muted = true;
          video.loop = false;
          video.playsInline = true;
          video.addEventListener('ended', () => {
            if (index === this.current && this.autoPlay && this.total > 1) this.go(1);
          });
        }
      });

      const youtubeIframes = this.cards.flatMap((card) => [...card.querySelectorAll('[data-ps-youtube-id]')]);
      if (!youtubeIframes.length) return;

      loadYouTubeApi().then((YT) => {
        youtubeIframes.forEach((iframe) => {
          if (!(iframe instanceof HTMLIFrameElement) || this.youtubePlayers.has(iframe)) return;

          const player = new YT.Player(iframe, {
            events: {
              onReady: () => {
                if (this.soundEnabled && this.cards[this.current]?.contains(iframe)) player.unMute?.();
                else player.mute?.();
                if (this.cards[this.current]?.contains(iframe)) {
                  player.seekTo(0, true);
                  player.playVideo();
                }
              },
              onStateChange: (event) => {
                if (event.data === YT.PlayerState.ENDED && this.cards[this.current]?.contains(iframe) && this.autoPlay && this.total > 1) {
                  this.go(1);
                }
              },
            },
          });

          this.youtubePlayers.set(iframe, player);
        });
      });
    }

    activeHasVideo() {
      const activeCard = this.cards[this.current];
      return Boolean(activeCard?.querySelector('video, [data-ps-youtube-id]'));
    }

    manageVideos() {
      this.cards.forEach((card, i) => {
        const isActive = i === this.current;
        const video = card.querySelector('video');
        if (video) {
          if (isActive) {
            video.muted = !this.soundEnabled;
            video.volume = this.soundEnabled ? 1 : 0;
            video.currentTime = 0;
            video.play().catch(() => {});
          } else {
            video.pause();
            video.currentTime = 0;
          }
        }

        const iframe = card.querySelector('[data-ps-youtube-id]');
        const player = iframe ? this.youtubePlayers.get(iframe) : null;
        if (player?.playVideo) {
          if (isActive) {
            if (this.soundEnabled) {
              player.unMute?.();
              player.setVolume?.(100);
            } else {
              player.mute?.();
            }
            player.seekTo?.(0, true);
            player.playVideo();
          } else {
            player.pauseVideo?.();
            player.seekTo?.(0, true);
          }
        }
      });
    }

    applySoundToActive() {
      const card = this.cards[this.current];
      if (!card) return;

      const video = card.querySelector('video');
      if (video) {
        video.muted = !this.soundEnabled;
        video.volume = this.soundEnabled ? 1 : 0;
        video.play().catch(() => {});
      }

      const iframe = card.querySelector('[data-ps-youtube-id]');
      const player = iframe ? this.youtubePlayers.get(iframe) : null;
      if (player?.playVideo) {
        if (this.soundEnabled) {
          player.unMute?.();
          player.setVolume?.(100);
        } else {
          player.mute?.();
        }
        player.playVideo?.();
      }
    }

    updateSoundButtons() {
      this.cards.forEach((card, index) => {
        const button = card.querySelector('[data-ps-unmute]');
        if (!button) return;
        const isActive = index === this.current;
        button.hidden = !isActive;
        button.classList.toggle('is-unmuted', this.soundEnabled);
        button.setAttribute('aria-pressed', this.soundEnabled ? 'true' : 'false');
        button.setAttribute('aria-label', this.soundEnabled ? 'Mute video' : 'Unmute video');
        const text = button.querySelector('.ps-unmute__text');
        if (text) text.textContent = this.soundEnabled ? 'Mute' : 'Sound';
      });
    }

    startAuto() {
      this.stopAuto();
      if (this.total <= 1 || !this.autoPlay || this.activeHasVideo()) return;
      this.timer = setTimeout(() => this.go(1), this.interval);
    }

    stopAuto() {
      clearTimeout(this.timer);
      this.timer = null;
    }

    resetAuto() {
      this.stopAuto();
      if (this.autoPlay) this.startAuto();
    }

    handleResize() {
      if (this.resizeRaf) cancelAnimationFrame(this.resizeRaf);
      this.resizeRaf = requestAnimationFrame(() => {
        this.position();
        this.resizeRaf = null;
      });
    }

    initTyping() {
      if (!window.TypingText) return;
      const info = this.root.querySelector('[data-ps-active-info]');
      if (!info) return;
      window.TypingText.initTyping(info, '[data-ps-active-name]', { baseDelay: 200, stagger: 0, speed: 72 });
      window.TypingText.initTyping(info, '[data-ps-active-price]', { baseDelay: 500, stagger: 0, speed: 72 });
    }
  }

  function initAll() {
    document.querySelectorAll('.product-showcase').forEach(el => {
      if (!el.dataset.initialized) {
        el.dataset.initialized = 'true';
        new ProductShowcase(el);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', e => {
      const section = e.target.querySelector('.product-showcase');
      if (section) {
        section.removeAttribute('data-initialized');
        initAll();
      }
    });
  }
})();
